<template>
  <div class="signin__container">
    <BasePageTitle>
      <template #title> Welcome to the CareerSafe Campus! </template>
      <template #subtitle>
        The CareerSafe program is an entirely online learning experience that's available any time,
        from anywhere.
        <span v-if="!isMobile">
          <a href="https://www.careersafeonline.com/" target="_blank">Learn more</a> about
          CareerSafe and our professional development solutions.
        </span>
      </template>
    </BasePageTitle>
    <div class="card__container">
      <KCard class="signin__card">
        <template #content>
          <h2 class="signin__header">Sign In to Your Account</h2>
          <BaseAlert
            id="invalid-credentials-alert"
            v-if="invalidCredentials"
            type="error"
            message="Invalid credentials"
          />
          <BaseAlert
            id="account-disabled-alert"
            v-if="accountDisabled"
            type="error"
            prefix="Account Disabled"
            message="Please contact <a href='https://www.careersafeonline.com/support/contact' target='_blank'>Customer Care</a> to reinstate your account."
          />
          <BaseAlert
            id="account-archived-alert"
            v-if="accountArchived"
            type="error"
            prefix="Account Archived"
            message="Please contact <a href='https://www.careersafeonline.com/support/contact' target='_blank'>Customer Care</a> to reinstate your account."
          />
          <form
            id="signin-form"
            class="signin-form"
            method="POST"
            :action="`${campus_url}/authenticate.k2`"
            @submit="submitForm"
            novalidate
          >
            <!-- email address -->
            <BaseInputTextValidated
              id="email"
              label="Email Address"
              name="email"
              type="email"
              placeholder="kasey@email.com"
              autocomplete="username"
            />
            <!-- password -->
            <BaseInputTextValidated
              id="password"
              label="Password"
              name="password"
              type="password"
              placeholder="Password"
              autocomplete="current-password"
            />
            <!-- redir hidden form field for redirection path after authentication -->
            <input type="hidden" name="redir" id="redir" v-model="redir" v-bind="redirAttrs" />
            <router-link
              class="signin__forgot-password-text"
              :to="{ name: 'ResetPasswordRequest' }"
            >
              Forgot password?
            </router-link>
          </form>
          <p class="signin__agreement">
            By clicking Sign In, you agree to our
            <a href="https://www.careersafeonline.com/terms-of-use" target="_blank">Terms of Use</a>
            and
            <a href="https://www.careersafeonline.com/privacy-policy" target="_blank"
              >Privacy Policy</a
            >.
          </p>
          <KButton id="submit-button" type="submit" form="signin-form" label="Sign In" />
          <div class="signin__create-account-text">
            Don't have an account?
            <router-link :to="{ name: 'CreateAccount', query: { redirect: route.query.redirect } }">
              Create Account
            </router-link>
            now.
          </div>
        </template>
      </KCard>
      <img
        v-if="isDesktop"
        class="signin__image"
        src="@/assets/images/Kasey-waving-peeking.svg"
        height="246px"
        alt="Kasey Koala waving peeking"
      />
    </div>

    <p v-if="isMobile" class="signin__description">
      <a href="https://www.careersafeonline.com/" target="_blank">Learn more</a> about CareerSafe
      and our professional development solutions.
    </p>
  </div>

  <!-- Modals -->
  <SignInSsoModal />

  <!-- Teleport to Nav Bar -->
  <Teleport to="#nav-bar-header-maintenance-banner">
    <div
      v-if="maintenanceBannerData && maintenanceBannerData.message !== ''"
      class="maintenance-banner-container"
    >
      <span
        ><KIcon
          icon="fa-solid fa-triangle-exclamation"
          class="maintenance-banner-icon"
          size="lg"
        /> </span
      ><span class="maintenance-banner-title">{{ maintenanceBannerData.title }}</span
      ><span>{{ ` ${maintenanceBannerData.message}` }}</span>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import BaseAlert from "@/components/base/BaseAlert.vue";
import { KButton, KCard, KIcon } from "@k2united/frontend_components";
import { computed } from "vue";
import BasePageTitle from "@/components/base/BasePageTitle.vue";
import BaseInputTextValidated from "@/components/base/BaseInputTextValidated.vue";
import SignInSsoModal from "@/components/signIn/SignInSsoModal.vue";
import { useUserStore } from "@/stores/user";
import { useRoute, useRouter } from "vue-router";
import { useResponsive } from "@/composables/useResponsive";
import { useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/yup";
import { object, string } from "yup";
import { useHead } from "@unhead/vue";
import publicApi from "@/api/publicApi";

useHead({
  title: "Sign In"
});

const campus_url = import.meta.env.VITE_APP_CAMPUS_URL;
const route = useRoute();
const router = useRouter();
const store = useUserStore();
const { isMobile, isDesktop } = useResponsive();

const invalidCredentials = computed(() => {
  return route.query.error === "2";
});
const accountDisabled = computed(() => {
  return route.query.error === "3";
});
const accountArchived = computed(() => {
  return route.query.error === "4";
});

// Maintenance Banner
const maintenanceBannerData = ref({ title: "", message: "" });
onMounted(async () => {
  try {
    const response = await publicApi.get(`utils/maintenance_notification`);
    maintenanceBannerData.value = response.data;
  } catch (error) {
    console.error(error);
  }
});

// Redirect to home page if already authenticated
if (store.isAuthenticated) {
  router.push({ name: "Home" });
}

// Form
const validationSchema = toTypedSchema(
  object({
    email: string().email().required().label("Email address").default(""),
    password: string().required().label("Password").default(""),
    redir: string().label("Redirect path").default("")
  })
);

const { submitForm, setFieldValue, defineField } = useForm({
  validationSchema
});
const [redir, redirAttrs] = defineField("redir");

if (typeof route.query.redirect === "string") {
  const redirect = route.query.redirect;
  delete route.query.redirect;
  const queryString = Object.keys(route.query)
    .map(function (k) {
      return `${k}=${route.query[k]}`;
    })
    .join("&");
  const url = queryString.length > 0 ? `${redirect}&${queryString}` : redirect;
  setFieldValue("redir", url);
}

/* Get redirect from local storage after session has timed out */
if (localStorage.getItem("CAREERSAFE_REDIRECT")) {
  const redirect = localStorage.getItem("CAREERSAFE_REDIRECT");

  const url = redirect ? redirect : "";
  setFieldValue("redir", url);

  localStorage.removeItem("CAREERSAFE_REDIRECT");
}
</script>

<style scoped lang="scss">
.signin__container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 0 auto;

  @include tablet {
    gap: 1.5rem;
    max-width: $layout-max-width;
  }
}

.card__container {
  margin: 0 auto;
  position: relative;
  width: min(40rem, 100%);
}

.signin__card {
  align-self: center;
  position: relative;
  z-index: 1;

  :deep(.k-card__body) {
    padding: 0.75rem 0.5rem 0.5rem 0.5rem;

    @include tablet {
      padding: 1.5rem 1rem 0.75rem 1rem;
    }
  }

  :deep(.k-card__content) {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }
}

.signin__description {
  color: $color-text-default;
  padding: 0.375rem 0.5rem;
  @include body;

  a {
    @include link;
  }
}

.signin-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

:deep(.k-field) {
  flex: 1;
  width: 100%;
}

.signin__header {
  @include header-5;
  color: $color-text-default;
}

.signin__forgot-password-text {
  @include link-semi-bold;
}

.signin__agreement {
  @include body-x-small;

  a {
    @include link-x-small;
  }
}

.signin__create-account-text {
  @include body;
  border-top: 1px solid $color-border-subtler;
  margin: 0 auto;
  padding: 0.75rem 5rem;
  text-align: center;

  @include tablet {
    margin: 0;
    padding: 1.5rem 1rem;
  }

  a {
    @include link;
  }
}

.signin__image {
  position: absolute;
  right: -130px;
  top: 10px;
}

:deep(.k-button) {
  @include tablet {
    align-self: flex-end;
    min-width: 8.75rem;
  }
}

.maintenance-banner-container {
  background-color: $color-background-warning;
  border-bottom: 1px solid $color-border-warning;
  border-top: 1px solid $color-border-warning;
  font-size: $font-12;
  padding: 0.75rem;
  text-align: center;
}
.maintenance-banner-icon {
  color: $color-icon-warning;
  padding-right: 0.5rem;
}
.maintenance-banner-title {
  font-size: $font-12;
  font-weight: 700;
}
</style>
